import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import {
  Warehouse,
  Plus,
  PencilSimple,
  Trash,
  Star,
  WarningCircle,
  Buildings,
} from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

const EMPTY = {
  id: null, name: "", address: "", lat: null, lng: null,
  city: "", state: "FL", country: "USA",
  contact_phone: "", manager_name: "", notes: "", is_default: false,
};

export default function Warehouses() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [missingFlCompanies, setMissingFlCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [assignDialog, setAssignDialog] = useState(null); // {warehouse}

  const load = async () => {
    try {
      const [{ data: ws }, { data: cs }] = await Promise.all([
        api.get("/warehouses"),
        user.role === "super_admin" ? api.get("/companies") : Promise.resolve({ data: [] }),
      ]);
      setRows(ws);
      setCompanies(cs);
      if (user.role === "super_admin") {
        const { data: m } = await api.get("/companies/florida-missing");
        setMissingFlCompanies(m.companies || []);
      }
    } catch (e) {
      toast.error("Failed to load warehouses");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!form.name.trim() || !form.address.trim()) {
      toast.error("Name and address are required");
      return;
    }
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (form.id) {
        delete payload.id;
        await api.patch(`/warehouses/${form.id}`, payload);
        toast.success("Warehouse updated");
      } else {
        await api.post("/warehouses", payload);
        toast.success("Warehouse added");
      }
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (w) => {
    if (!confirm(`Delete "${w.name}"? All company assignments will be removed.`)) return;
    try {
      await api.delete(`/warehouses/${w.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const startEdit = (w) => {
    setForm({
      id: w.id, name: w.name || "", address: w.address || "",
      lat: w.lat ?? null, lng: w.lng ?? null,
      city: w.city || "", state: w.state || "", country: w.country || "USA",
      contact_phone: w.contact_phone || "", manager_name: w.manager_name || "",
      notes: w.notes || "", is_default: !!w.is_default,
    });
    setOpen(true);
  };

  const startNew = () => { setForm(EMPTY); setOpen(true); };

  const toggleAssign = async (warehouseId, companyId, currentlyAssigned) => {
    try {
      if (currentlyAssigned) {
        await api.delete(`/companies/${companyId}/warehouses/${warehouseId}`);
        toast.success("Unassigned");
      } else {
        await api.post(`/companies/${companyId}/warehouses`, { warehouse_id: warehouseId });
        toast.success("Assigned");
      }
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const isSuper = user.role === "super_admin";

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="warehouses-page">
      <Toaster position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
            Infrastructure
          </div>
          <h1
            className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1"
            style={{ fontFamily: "Work Sans" }}
          >
            Warehouses
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {isSuper
              ? "Manage the global warehouse pool and assign warehouses to courier companies."
              : "Warehouses assigned to your company."}
          </p>
        </div>
        {isSuper && (
          <Button className="bg-[#002BF6] hover:bg-[#0021C7] text-white" onClick={startNew} data-testid="new-warehouse-btn">
            <Plus size={15} weight="bold" className="mr-1.5" /> New Warehouse
          </Button>
        )}
      </div>

      {isSuper && missingFlCompanies.length > 0 && (
        <div className="mb-5 bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3" data-testid="missing-fl-banner">
          <WarningCircle size={18} weight="bold" className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <div className="font-semibold mb-1">
              {missingFlCompanies.length} {missingFlCompanies.length === 1 ? "company has" : "companies have"} no Florida warehouse assigned
            </div>
            <div className="text-xs text-amber-800">
              {missingFlCompanies.map((c) => c.name).join(" · ")}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]" data-testid="warehouses-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3 font-semibold">Warehouse</th>
              <th className="px-5 py-3 font-semibold">Location</th>
              <th className="px-5 py-3 font-semibold">Manager</th>
              <th className="px-5 py-3 font-semibold">Phone</th>
              {isSuper && <th className="px-5 py-3 font-semibold">Assigned to</th>}
              {isSuper && <th className="px-5 py-3 font-semibold text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={isSuper ? 6 : 4} className="px-5 py-12 text-center text-zinc-500">
                  <Warehouse size={32} className="mx-auto mb-2 text-zinc-300" />
                  No warehouses yet.{isSuper && " Add the first one to start assigning to companies."}
                </td>
              </tr>
            ) : rows.map((w) => (
              <tr key={w.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`warehouse-row-${w.id}`}>
                <td className="px-5 py-4">
                  <div className="font-medium text-zinc-900 flex items-center gap-1.5">
                    {w.is_default && <Star size={12} weight="fill" className="text-amber-500" />}
                    {w.name}
                  </div>
                  {(w.lat != null && w.lng != null) && (
                    <div className="text-[10px] text-emerald-600 font-mono mt-0.5">● geocoded</div>
                  )}
                </td>
                <td className="px-5 py-4 text-zinc-700 max-w-[300px]">
                  <div className="text-sm">{w.address}</div>
                  <div className="text-xs text-zinc-500">{[w.city, w.state, w.country].filter(Boolean).join(", ")}</div>
                </td>
                <td className="px-5 py-4 text-zinc-700">{w.manager_name || "—"}</td>
                <td className="px-5 py-4 text-zinc-700 font-mono text-xs">{w.contact_phone || "—"}</td>
                {isSuper && (
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs text-zinc-600">
                        {w.assigned_company_ids.length} {w.assigned_company_ids.length === 1 ? "company" : "companies"}
                      </span>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                              onClick={() => setAssignDialog(w)}
                              data-testid={`manage-assignments-${w.id}`}>
                        <Buildings size={12} className="mr-1" /> Manage
                      </Button>
                    </div>
                  </td>
                )}
                {isSuper && (
                  <td className="px-5 py-4 text-right">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(w)} data-testid={`edit-warehouse-${w.id}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(w)} data-testid={`delete-warehouse-${w.id}`} className="text-red-600 hover:text-red-700">
                      <Trash size={14} />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl p-0 max-h-[90vh] gap-0 overflow-hidden flex flex-col" data-testid="warehouse-dialog">
          <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-100 shrink-0">
            <DialogTitle>{form.id ? "Edit warehouse" : "Add warehouse"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 overflow-y-auto px-5 sm:px-6 py-4 flex-1">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Name</Label>
              <Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="wh-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Address {form.lat != null && <span className="text-emerald-600 normal-case font-normal ml-1">● geocoded</span>}
              </Label>
              <AddressAutocomplete
                value={form.address}
                onChange={(v) => setForm({ ...form, address: v, lat: null, lng: null })}
                onLocationChange={(lat, lng) => setForm((f) => ({ ...f, lat, lng }))}
                rows={2}
                className="mt-1.5"
                dataTestId="wh-address"
                country="US"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">City</Label>
                <Input className="mt-1.5" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="wh-city" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">State</Label>
                <Input className="mt-1.5" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="wh-state" placeholder="FL" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Country</Label>
                <Input className="mt-1.5" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="wh-country" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Manager</Label>
                <Input className="mt-1.5" value={form.manager_name} onChange={(e) => setForm({ ...form, manager_name: e.target.value })} data-testid="wh-manager" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Contact phone</Label>
                <Input className="mt-1.5" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="wh-phone" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Notes</Label>
              <Textarea className="mt-1.5" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="wh-notes" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer pt-1">
              <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="rounded" data-testid="wh-is-default" />
              <span className="text-sm">Mark as default warehouse</span>
            </label>
          </div>
          <DialogFooter className="px-5 sm:px-6 py-4 border-t border-zinc-100 bg-white shrink-0 gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="save-warehouse-btn">
              {form.id ? "Save changes" : "Add warehouse"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign companies dialog */}
      <Dialog open={!!assignDialog} onOpenChange={(o) => !o && setAssignDialog(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md" data-testid="assign-companies-dialog">
          <DialogHeader>
            <DialogTitle>Assign companies — {assignDialog?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {companies.map((c) => {
              const assigned = assignDialog?.assigned_company_ids?.includes(c.id);
              return (
                <label
                  key={c.id}
                  className="flex items-center justify-between border border-zinc-200 rounded p-3 cursor-pointer hover:bg-zinc-50"
                  data-testid={`assign-row-${c.id}`}
                >
                  <div>
                    <div className="text-sm font-medium text-zinc-900">{c.name}</div>
                    <div className="text-[11px] text-zinc-500">{c.contact_email}</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={!!assigned}
                    onChange={() => toggleAssign(assignDialog.id, c.id, assigned)}
                    className="rounded h-4 w-4"
                    data-testid={`assign-checkbox-${c.id}`}
                  />
                </label>
              );
            })}
            {companies.length === 0 && (
              <div className="text-sm text-zinc-500 py-6 text-center">No companies yet.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
